import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable, Subject } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { GoogleMap } from '@capacitor/google-maps';
import { buildGoogleMapsScriptUrl } from './utils/runtime-config.util';

// Definimos una interfaz para Window para añadir las propiedades dinámicas
interface WindowWithGoogleCallback extends Window {
  [key: string]: any; // Permite índices de cadena para las callbacks dinámicas
  google?: any;
}

// Tipo para las claves de estilos del mapa
export const MAP_STYLE_KEYS = ['standard', 'silver', 'night', 'retro', 'aubergine'] as const;
export type MapStyleType = typeof MAP_STYLE_KEYS[number];

// Interfaz para el resultado de la autocompletación
export interface PlaceResult {
  address: string;
  location?: {
    lat: number;
    lng: number;
  };
  placeId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsService {
  private isLoaded = false;
  private loadingPromise: Promise<void> | null = null;
  private callbacks: Function[] = [];
  private preloadedNativeMap: any = null;

  // Estilos de mapa disponibles
  mapStyles: Record<MapStyleType, any[]> = {
    standard: [], // Estilo por defecto
    silver: [
      {
        "elementType": "geometry",
        "stylers": [{"color": "#f5f5f5"}]
      },
      {
        "elementType": "labels.icon",
        "stylers": [{"visibility": "off"}]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#616161"}]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [{"color": "#f5f5f5"}]
      },
      {
        "featureType": "administrative.land_parcel",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#bdbdbd"}]
      },
      {
        "featureType": "poi",
        "elementType": "geometry",
        "stylers": [{"color": "#eeeeee"}]
      },
      {
        "featureType": "poi",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#757575"}]
      },
      {
        "featureType": "poi.park",
        "elementType": "geometry",
        "stylers": [{"color": "#e5e5e5"}]
      },
      {
        "featureType": "poi.park",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#9e9e9e"}]
      },
      {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [{"color": "#ffffff"}]
      },
      {
        "featureType": "road.arterial",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#757575"}]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [{"color": "#dadada"}]
      },
      {
        "featureType": "road.highway",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#616161"}]
      },
      {
        "featureType": "road.local",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#9e9e9e"}]
      },
      {
        "featureType": "transit.line",
        "elementType": "geometry",
        "stylers": [{"color": "#e5e5e5"}]
      },
      {
        "featureType": "transit.station",
        "elementType": "geometry",
        "stylers": [{"color": "#eeeeee"}]
      },
      {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [{"color": "#c9c9c9"}]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#9e9e9e"}]
      }
    ],
    night: [
      {
        "elementType": "geometry",
        "stylers": [{"color": "#242f3e"}]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#746855"}]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [{"color": "#242f3e"}]
      },
      {
        "featureType": "administrative.locality",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#d59563"}]
      },
      {
        "featureType": "poi",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#d59563"}]
      },
      {
        "featureType": "poi.park",
        "elementType": "geometry",
        "stylers": [{"color": "#263c3f"}]
      },
      {
        "featureType": "poi.park",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#6b9a76"}]
      },
      {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [{"color": "#38414e"}]
      },
      {
        "featureType": "road",
        "elementType": "geometry.stroke",
        "stylers": [{"color": "#212a37"}]
      },
      {
        "featureType": "road",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#9ca5b3"}]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [{"color": "#746855"}]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry.stroke",
        "stylers": [{"color": "#1f2835"}]
      },
      {
        "featureType": "road.highway",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#f3d19c"}]
      },
      {
        "featureType": "transit",
        "elementType": "geometry",
        "stylers": [{"color": "#2f3948"}]
      },
      {
        "featureType": "transit.station",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#d59563"}]
      },
      {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [{"color": "#17263c"}]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#515c6d"}]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.stroke",
        "stylers": [{"color": "#17263c"}]
      }
    ],
    retro: [
      {
        "elementType": "geometry",
        "stylers": [{"color": "#ebe3cd"}]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#523735"}]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [{"color": "#f5f1e6"}]
      },
      {
        "featureType": "administrative",
        "elementType": "geometry.stroke",
        "stylers": [{"color": "#c9b2a6"}]
      },
      {
        "featureType": "administrative.land_parcel",
        "elementType": "geometry.stroke",
        "stylers": [{"color": "#dcd2be"}]
      },
      {
        "featureType": "administrative.land_parcel",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#ae9e90"}]
      },
      {
        "featureType": "landscape.natural",
        "elementType": "geometry",
        "stylers": [{"color": "#dfd2ae"}]
      },
      {
        "featureType": "poi",
        "elementType": "geometry",
        "stylers": [{"color": "#dfd2ae"}]
      },
      {
        "featureType": "poi",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#93817c"}]
      },
      {
        "featureType": "poi.park",
        "elementType": "geometry.fill",
        "stylers": [{"color": "#a5b076"}]
      },
      {
        "featureType": "poi.park",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#447530"}]
      },
      {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [{"color": "#f5f1e6"}]
      },
      {
        "featureType": "road.arterial",
        "elementType": "geometry",
        "stylers": [{"color": "#fdfcf8"}]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [{"color": "#f8c967"}]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry.stroke",
        "stylers": [{"color": "#e9bc62"}]
      },
      {
        "featureType": "road.highway.controlled_access",
        "elementType": "geometry",
        "stylers": [{"color": "#e98d58"}]
      },
      {
        "featureType": "road.highway.controlled_access",
        "elementType": "geometry.stroke",
        "stylers": [{"color": "#db8555"}]
      },
      {
        "featureType": "road.local",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#806b63"}]
      },
      {
        "featureType": "transit.line",
        "elementType": "geometry",
        "stylers": [{"color": "#dfd2ae"}]
      },
      {
        "featureType": "transit.line",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#8f7d77"}]
      },
      {
        "featureType": "transit.line",
        "elementType": "labels.text.stroke",
        "stylers": [{"color": "#ebe3cd"}]
      },
      {
        "featureType": "transit.station",
        "elementType": "geometry",
        "stylers": [{"color": "#dfd2ae"}]
      },
      {
        "featureType": "water",
        "elementType": "geometry.fill",
        "stylers": [{"color": "#b9d3c2"}]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#92998d"}]
      }
    ],
    aubergine: [
      {
        "elementType": "geometry",
        "stylers": [{"color": "#1d2c4d"}]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#8ec3b9"}]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [{"color": "#1a3646"}]
      },
      {
        "featureType": "administrative.country",
        "elementType": "geometry.stroke",
        "stylers": [{"color": "#4b6878"}]
      },
      {
        "featureType": "administrative.land_parcel",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#64779e"}]
      },
      {
        "featureType": "administrative.province",
        "elementType": "geometry.stroke",
        "stylers": [{"color": "#4b6878"}]
      },
      {
        "featureType": "landscape.man_made",
        "elementType": "geometry.stroke",
        "stylers": [{"color": "#334e87"}]
      },
      {
        "featureType": "landscape.natural",
        "elementType": "geometry",
        "stylers": [{"color": "#023e58"}]
      },
      {
        "featureType": "poi",
        "elementType": "geometry",
        "stylers": [{"color": "#283d6a"}]
      },
      {
        "featureType": "poi",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#6f9ba5"}]
      },
      {
        "featureType": "poi",
        "elementType": "labels.text.stroke",
        "stylers": [{"color": "#1d2c4d"}]
      },
      {
        "featureType": "poi.park",
        "elementType": "geometry.fill",
        "stylers": [{"color": "#023e58"}]
      },
      {
        "featureType": "poi.park",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#3C7680"}]
      },
      {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [{"color": "#304a7d"}]
      },
      {
        "featureType": "road",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#98a5be"}]
      },
      {
        "featureType": "road",
        "elementType": "labels.text.stroke",
        "stylers": [{"color": "#1d2c4d"}]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [{"color": "#2c6675"}]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry.stroke",
        "stylers": [{"color": "#255763"}]
      },
      {
        "featureType": "road.highway",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#b0d5ce"}]
      },
      {
        "featureType": "road.highway",
        "elementType": "labels.text.stroke",
        "stylers": [{"color": "#023e58"}]
      },
      {
        "featureType": "transit",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#98a5be"}]
      },
      {
        "featureType": "transit",
        "elementType": "labels.text.stroke",
        "stylers": [{"color": "#1d2c4d"}]
      },
      {
        "featureType": "transit.line",
        "elementType": "geometry.fill",
        "stylers": [{"color": "#283d6a"}]
      },
      {
        "featureType": "transit.station",
        "elementType": "geometry",
        "stylers": [{"color": "#3a4762"}]
      },
      {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [{"color": "#0e1626"}]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [{"color": "#4e6d70"}]
      }
    ]
  };

  // Definir un estilo personalizado para ocultar POIs y elementos innecesarios
  customMapControls = {
    // Ocultar completamente los puntos de interés
    hidePois: [
      {
        "featureType": "poi",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "poi.park",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "poi.business",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "poi.attraction",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "poi.government",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "poi.medical",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "poi.place_of_worship",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "poi.school",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "featureType": "poi.sports_complex",
        "stylers": [{ "visibility": "off" }]
      },
      // Reducir visibilidad de transporte
      {
        "featureType": "transit",
        "stylers": [{ "visibility": "off" }]
      },
      // Ocultar etiquetas de navegación
      {
        "elementType": "labels.icon",
        "stylers": [{ "visibility": "off" }]
      }
    ]
  };

  constructor() { }

  /**
   * Carga la API de Google Maps si aún no está cargada
   * @returns Una promesa que se resuelve cuando la API se ha cargado
   */
  loadGoogleMaps(): Promise<void> {
    // Si ya está cargada, devolvemos una promesa resuelta
    if (this.isLoaded) {
      return Promise.resolve();
    }

    // Si ya estamos cargando, devolvemos la promesa en curso
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Crear una nueva promesa para cargar el script
    this.loadingPromise = new Promise<void>((resolve, reject) => {
      // Tratar window como WindowWithGoogleCallback para permitir propiedades dinámicas
      const windowWithCallback = window as WindowWithGoogleCallback;

      // Verificar si el script ya existe
      if (windowWithCallback.google && windowWithCallback.google.maps) {
        this.isLoaded = true;
        resolve();
        return;
      }

      // Crear una callback global para cuando Google Maps se cargue
      const callbackName = 'gmapsCallback_' + Math.random().toString(36).substring(2, 15);

      // Asignar la callback a window con el tipo correcto
      windowWithCallback[callbackName] = () => {
        this.isLoaded = true;

        // Ejecutar los callbacks que estaban esperando
        this.callbacks.forEach(callback => callback());
        this.callbacks = [];

        resolve();
        delete windowWithCallback[callbackName];
      };

      // Crear el script con la clave API desde environment
      const script = document.createElement('script');
      script.src = buildGoogleMapsScriptUrl(
        environment.googleMaps.apiKey,
        callbackName
      );
      script.async = true;
      script.defer = true;
      script.onerror = (error) => {
        reject('Google Maps script failed to load: ' + error);
      };

      // Añadir el script al documento
      document.body.appendChild(script);
    });

    return this.loadingPromise;
  }

  /**
   * Preload a small native map surface to warm native plugins and reduce
   * first-open latency. The map is destroyed after a short delay.
   */
  async preloadNativeMap(): Promise<void> {
    try {
      const platform = Capacitor.getPlatform && typeof Capacitor.getPlatform === 'function' ? Capacitor.getPlatform() : undefined;
      if (!(platform === 'android' || platform === 'ios')) return;

      // iOS: CapacitorGoogleMaps requires the element to be in the visible
      // viewport with a valid frame. Creating a map with an off-screen 1×1
      // container causes Map.swift to nil-crash when unwrapping the view frame.
      // Skip the preload warm-up on iOS entirely — it is a non-critical optimisation.
      if (platform === 'ios') return;

      // If already preloaded, skip
      if (this.preloadedNativeMap) return;

      // Create a tiny offscreen container for the native map
      const container = document.createElement('div');
      container.id = 'gmaps-preload-container';
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      container.style.width = '1px';
      container.style.height = '1px';
      document.body.appendChild(container);

      try {
        const map = await GoogleMap.create({
          apiKey: environment.googleMaps?.apiKey || '',
          id: 'gmaps-preload',
          element: container as HTMLElement,
          forceCreate: true,
          config: {
            center: { lat: 40.4168, lng: -3.7038 },
            zoom: 1,
            width: 1,
            height: 1,
            devicePixelRatio: window.devicePixelRatio,
            androidLiteMode: false,
            styles: [],
          }
        });

        this.preloadedNativeMap = map;

        // Destroy after a short delay to free resources; warming effect remains.
        setTimeout(async () => {
          try {
            await (this.preloadedNativeMap as any).destroy?.();
          } catch (e) {}
          try { document.body.removeChild(container); } catch (e) {}
          this.preloadedNativeMap = null;
        }, 2500);
      } catch (createErr) {
        try { document.body.removeChild(container); } catch (e) {}
        this.preloadedNativeMap = null;
      }
    } catch (e) {
    }
  }

  /**
   * Registra un callback para ejecutar cuando la API de Google Maps esté disponible
   * @param callback La función a ejecutar cuando Google Maps esté disponible
   */
  onMapsReady(callback: Function): void {
    if (this.isLoaded) {
      callback();
    } else {
      this.callbacks.push(callback);
      this.loadGoogleMaps();
    }
  }

  /**
   * Obtiene los estilos del mapa combinados para el tipo especificado
   * @param styleType El tipo de estilo a aplicar
   * @returns Array con los estilos combinados (estilos del mapa + controles personalizados)
   */
  getMapStyles(styleType: MapStyleType): any[] {
    return [...this.mapStyles[styleType], ...this.customMapControls.hidePois];
  }

  /**
   * Inicializa el autocompletado de Google Places en un elemento de entrada
   * @param inputElement El elemento HTML de entrada para aplicar el autocompletado
   * @returns Un Observable que emite el resultado del lugar seleccionado
   */
  initAutocomplete(inputElement: HTMLInputElement): Observable<PlaceResult> {
    const placeSubject = new Subject<PlaceResult>();

    // Asegurarse de que la API de Google Maps esté cargada
    this.onMapsReady(() => {
      const windowWithGoogle = window as WindowWithGoogleCallback;

      if (!windowWithGoogle.google || !windowWithGoogle.google.maps || !windowWithGoogle.google.maps.places) {
        return;
      }

      // Opciones de autocompletado (puedes personalizar según necesidades)
      const options = {
        types: ['address'], // Tipos de resultados a mostrar
        componentRestrictions: { country: [] } // Sin restricción de país
      };

      // Crear el autocompletado
      const autocomplete = new windowWithGoogle.google.maps.places.Autocomplete(inputElement, options);

      // Escuchar el evento place_changed
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();

        if (!place || !place.formatted_address) {
          return;
        }

        // Crear el resultado con la dirección formateada
        const result: PlaceResult = {
          address: place.formatted_address
        };

        // Añadir coordenadas si están disponibles
        if (place.geometry && place.geometry.location) {
          result.location = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };
        }

        // Añadir el ID del lugar si está disponible
        if (place.place_id) {
          result.placeId = place.place_id;
        }

        // Emitir el resultado
        placeSubject.next(result);
      });
    });

    return placeSubject.asObservable();
  }
}
